import { Plus, Users } from 'lucide-react';
import { ClientListCard } from '../../../components/ClientListCard';
import type { ClientUser } from '../../../api/clientsApi';

type ClientsGridProps = {
  isPending: boolean;
  hasAnyClients: boolean;
  filtered: ClientUser[];
  onCreate: () => void;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
};

export function ClientsGrid({
  isPending,
  hasAnyClients,
  filtered,
  onCreate,
  onView,
  onEdit,
  onDelete,
}: ClientsGridProps) {
  if (isPending) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="camp-skeleton h-[240px] rounded-2xl border border-[var(--dash-line)]" />
        ))}
      </div>
    );
  }

  if (!hasAnyClients) {
    return (
      <div className="flex min-h-[42vh] flex-col items-center justify-center rounded-3xl border border-dashed border-[var(--dash-line)] bg-white px-6 py-12 text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--dash-brand-soft)] text-[var(--dash-brand)]">
          <Users className="h-7 w-7" />
        </div>
        <h2 className="m-0 text-lg font-semibold text-[var(--dash-ink)]">No clients yet</h2>
        <p className="mt-2 mb-6 max-w-sm text-sm text-[var(--dash-muted)]">
          Add your first client to manage their campaigns and billing under your agency.
        </p>
        <button
          type="button"
          onClick={onCreate}
          className="dash-btn-gradient inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white"
        >
          <Plus className="h-4 w-4" />
          Create Client
        </button>
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div className="flex min-h-[28vh] flex-col items-center justify-center rounded-3xl border border-dashed border-[var(--dash-line)] bg-white px-6 py-10 text-center">
        <h2 className="m-0 text-base font-semibold text-[var(--dash-ink)]">No matching clients</h2>
        <p className="mt-2 mb-0 text-sm text-[var(--dash-muted)]">
          Try a different search or clear filters.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      {filtered.map((c, i) => (
        <ClientListCard
          key={c._id}
          client={c}
          index={i}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}
